import { useEffect, useRef } from 'react'
import { CATEGORY_LABELS } from '@/constants/products'
import { createEditorialRevealAnimation } from '@/lib/gsap/editorial-reveals'
import { createProductParallaxAnimation } from '@/lib/gsap/product-parallax'
import { formatPriceInCents } from '@/lib/utils/format-currency'
import type { Product } from '@/schemas/product.schema'
import { useProductsByCategory } from '@/services/products/use-products'

export interface ProductPageModelProps {
  product: Product
}

const MAX_SIBLINGS = 3

export function useProductPageModel({ product }: ProductPageModelProps) {
  const { data: categoryProducts } = useProductsByCategory(product.category)
  const siblings = categoryProducts
    .filter((sibling) => sibling.slug !== product.slug)
    .slice(0, MAX_SIBLINGS)

  const photoRef = useRef<HTMLDivElement>(null)
  const siblingsContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const parallax = createProductParallaxAnimation(photoRef.current)
    return () => parallax?.revert()
  }, [])

  useEffect(() => {
    if (siblings.length === 0) {
      return
    }

    const reveal = createEditorialRevealAnimation(siblingsContainerRef.current)
    return () => reveal?.revert()
  }, [siblings])

  return {
    product,
    categoryLabel: CATEGORY_LABELS[product.category],
    formattedPrice: formatPriceInCents(product.priceInCents),
    siblings,
    hasSiblings: siblings.length > 0,
    photoRef,
    siblingsContainerRef,
  }
}
